// Phase 1 — Profile-Specific Data Integration (Feb 2026).
//
// MedicalConsentBanner — rendered ABOVE the L3 form when the active
// profile_key is medical-sensitive (sports_doctor, sport_psychologist).
//
// Why it exists:
//   • Backend `PUT /api/users/me/profile-data/profile/{profile_key}`
//     rejects substantive saves with 400 `errors.medical_research_consent_required`
//     unless `consent_aggregated_research_use=True` is included in the
//     payload. Surfacing this gate visually prevents the user from hitting
//     that 400 blindly and explains the legal context (research-only use,
//     no marketing/segmentation/rewards usage).
//   • Read-only banner — does NOT mutate the consent flag. The flag itself
//     is the first checkbox of the L3 form so the toggle stays inside the
//     normal save flow.
//
// No PII. No backend calls. Pure presentation.

import React from "react";
import { ShieldAlert, Info } from "lucide-react";

const MedicalConsentBanner = ({ t, consented }) => (
  <div
    className={`rounded-xl border p-4 sm:p-5 ${
      consented
        ? "border-emerald-200 bg-emerald-50/60"
        : "border-amber-200 bg-amber-50/60"
    }`}
    data-testid="medical-consent-banner"
    data-consented={consented ? "true" : "false"}
  >
    <div className="flex items-start gap-3">
      <ShieldAlert
        className={`h-5 w-5 shrink-0 mt-0.5 ${
          consented ? "text-emerald-600" : "text-amber-600"
        }`}
      />
      <div className="min-w-0">
        <h4
          className={`text-sm font-semibold ${
            consented ? "text-emerald-900" : "text-amber-900"
          }`}
          data-testid="medical-consent-banner-title"
        >
          {t("profileBlock.medicalConsent.title")}
        </h4>
        <p
          className={`text-xs mt-1 leading-relaxed ${
            consented ? "text-emerald-800" : "text-amber-800"
          }`}
          data-testid="medical-consent-banner-body"
        >
          {t("profileBlock.medicalConsent.body")}
        </p>
        {!consented && (
          <div
            className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700"
            data-testid="medical-consent-banner-note"
          >
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{t("profileBlock.medicalConsent.note")}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default MedicalConsentBanner;
